(() => {
  if (customElements.get('sn-cz-builder')) return;

  const check = '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="m3 8 3 3 7-7" stroke="currentColor" stroke-width="1.5"/></svg>';
  const escape = (value) => String(value ?? '').replace(/[&<>"']/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character]));

  class SnCzBuilder extends HTMLElement {
    connectedCallback() {
      this.frame = requestAnimationFrame(() => this.initialize());
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.events?.abort();
      this.initialized = false;
    }

    initialize() {
      if (this.initialized || !this.isConnected) return;
      const data = this.querySelector('[data-czb-data]');
      const copy = this.querySelector('[data-czb-copy]');
      if (!data || !copy) return;
      this.sets = JSON.parse(data.textContent).filter((set) => /^[1-5]$/.test(String(set.id)));
      if (!this.sets.length) return;
      this.copy = JSON.parse(copy.textContent);
      this.initialized = true;
      this.events = new AbortController();
      this.preferences ??= new Map();
      this.purchasing = false;
      this.set = this.fromUrl();
      this.media = window.matchMedia('(max-width: 760px)');
      this.listen(this.media, 'change', () => this.positionSelection());
      this.listen(window, 'pageshow', () => this.setBusy(false));
      this.listen(window, 'popstate', () => {
        if (this.purchasing) return;
        this.set = this.fromUrl();
        this.update();
      });
      this.listen(this, 'click', (event) => this.onClick(event));
      this.listen(this, 'change', (event) => {
        const field = event.target.closest('[data-czb-lengths] select');
        const definition = field && this.lengthFields().find((item) => item.key === field.name);
        if (definition && !this.purchasing) {
          this.preference()[field.name] = definition.lengths.some((length) => String(length) === field.value) ? field.value : '';
          field.removeAttribute('aria-invalid');
          this.showStatus(this.canPurchase() ? '' : this.copy.unavailable);
        }
      });
      this.listen(this, 'shopify:block:select', (event) => {
        const button = event.target.closest('[data-czb-set]');
        if (button) this.selectSet(button.dataset.czbSet);
      });
      this.positionSelection();
      this.update();
      this.syncUrl();
    }

    listen(target, event, handler) {
      target.addEventListener(event, handler, { signal: this.events.signal });
    }

    find(selector) {
      return this.querySelector(selector);
    }

    fromUrl() {
      const selected = this.dataset.czbNative === 'true' && !window.Shopify?.designMode ? this.dataset.czbInitialSet : new URL(window.location.href).searchParams.get('selected');
      return this.sets.find((set) => set.id === selected) || this.sets.find((set) => set.id === this.dataset.czbInitialSet) || this.sets.find((set) => set.id === '5') || this.sets[0];
    }

    syncUrl() {
      const url = new URL(window.location.href);
      if (this.dataset.czbNative === 'true' && !window.Shopify?.designMode) url.searchParams.delete('selected');
      else url.searchParams.set('selected', this.set.id);
      history.replaceState(history.state, '', url);
    }

    selectSet(id) {
      if (this.purchasing) return;
      const next = this.sets.find((set) => set.id === id);
      if (!next) return;
      this.set = next;
      this.update();
      this.syncUrl();
    }

    positionSelection() {
      const selection = this.find('.sn-czb-set-selection');
      const focused = document.activeElement;
      const restoreFocus = selection.contains(focused);
      if (this.media.matches) this.find('.sn-czb-gallery').insertBefore(selection, this.find('.sn-czb-gallery-bottom'));
      else this.find('.sn-czb-config').insertBefore(selection, this.find('.sn-czb-included'));
      if (restoreFocus) focused.focus({ preventScroll: true });
    }

    preference() {
      if (!this.preferences.has(this.set.id)) this.preferences.set(this.set.id, { chain1: '', chain2: '', bracelet: '' });
      return this.preferences.get(this.set.id);
    }

    lengthFields() {
      if (this.set.category === 'layered') {
        return [
          { key: 'chain1', label: this.copy.chain_1, lengths: [22, 24] },
          { key: 'chain2', label: this.copy.chain_2, lengths: [22, 24] }
        ];
      }
      return [
        { key: 'chain1', label: this.copy.chain, lengths: [22, 24] },
        { key: 'bracelet', label: this.copy.bracelet, lengths: [7, 8, 9] }
      ];
    }

    imageEntries() {
      return Array.isArray(this.set.images)
        ? this.set.images
        : Object.values(this.set.images || {}).filter(Boolean).map((src) => ({ src }));
    }

    images() {
      return this.imageEntries().map((image) => image.src).filter(Boolean);
    }

    imageUrl(source, width) {
      const url = new URL(source, window.location.href);
      url.searchParams.set('width', width);
      return url.href;
    }

    setImage(image, source, alt) {
      if (!image || !source) return;
      const details = this.imageEntries().find((item) => item.src === source);
      if (details?.width && details?.height) {
        image.width = details.width;
        image.height = details.height;
      }
      image.hidden = false;
      image.srcset = [400, 700, 750, 800, 1000, 1400].map((width) => `${this.imageUrl(source, width)} ${width}w`).join(', ');
      image.src = source;
      image.alt = alt;
    }

    selectPhoto(source) {
      const index = this.images().indexOf(source);
      let image = this.find('.sn-czb-main-image img');
      if (source && !image) {
        image = document.createElement('img');
        image.sizes = '(max-width: 760px) 100vw, 45vw';
        this.find('.sn-czb-main-image').appendChild(image);
      }
      if (!source && image) {
        image.remove();
        image = null;
      }
      const details = this.imageEntries().find((item) => item.src === source);
      this.setImage(image, source, details?.alt || this.set.name);
      this.querySelectorAll('[data-czb-photo]').forEach((button) => button.setAttribute('aria-pressed', String(Number(button.dataset.czbPhoto) === index)));
    }

    updateLengths() {
      const preferences = this.preference();
      this.find('[data-czb-lengths]').innerHTML = this.lengthFields().map(({ key, label, lengths }) => {
        if (!lengths.some((length) => String(length) === preferences[key])) preferences[key] = '';
        return `<label class="sn-czb-length-field"><span>${escape(label)}</span><select name="${key}" aria-label="${escape(label)}" required><option value="">${escape(this.copy.choose_length)}</option>${lengths.map((length) => `<option value="${length}"${preferences[key] === String(length) ? ' selected' : ''}>${length} ${escape(this.copy.inches)}</option>`).join('')}</select></label>`;
      }).join('');
    }

    update() {
      this.find('[data-czb-title]').textContent = this.set.name;
      const price = this.find('[data-czb-price]');
      this.find('[data-czb-current-price]').textContent = this.set.price || '';
      price.hidden = !this.set.price;
      const compare = this.find('[data-czb-compare]');
      const saleBadge = this.find('[data-czb-sale-badge]');
      const showSale = Boolean(this.set.price && this.set.comparePrice);
      price.classList.toggle('sn-czb-sale-price', showSale);
      const saleLabel = this.find('[data-czb-sale-label]');
      if (saleLabel) saleLabel.hidden = !showSale;
      if (compare) {
        this.find('[data-czb-compare-price]').textContent = this.set.comparePrice || '';
        compare.hidden = !showSale;
      }
      if (saleBadge) {
        const discountPercent = Number(this.set.discountPercent);
        const discountLabel = this.find('[data-czb-discount-percent]');
        if (discountLabel) discountLabel.textContent = Number.isFinite(discountPercent) ? String(discountPercent) : '';
        saleBadge.hidden = !showSale || !Number.isFinite(discountPercent) || discountPercent <= 0;
      }
      this.updateInstallments();
      const description = this.find('[data-czb-description]');
      description.textContent = this.set.description || '';
      description.hidden = !this.set.description;
      this.find('[data-czb-set-number]').textContent = `${this.copy.set} ${this.set.id.padStart(2, '0')} / ${String(this.sets.length).padStart(2, '0')}`;
      const parts = this.set.parts.filter(Boolean);
      this.find('[data-czb-parts]').innerHTML = parts.map((part) => `<li>${check}<span>${escape(part)}</span></li>`).join('');
      this.querySelectorAll('[data-czb-set]').forEach((link) => link.setAttribute('aria-current', link.dataset.czbSet === this.set.id ? 'page' : 'false'));
      this.find('[data-czb-thumbnails]').innerHTML = this.images().map((source, index) => `<button type="button" data-czb-photo="${index}" aria-pressed="false" aria-label="${escape(this.copy.view_photo)} ${index + 1} — ${escape(this.set.name)}"><img src="${escape(this.imageUrl(source, 160))}" srcset="${escape(this.imageUrl(source, 160))} 160w, ${escape(this.imageUrl(source, 240))} 240w" sizes="(min-width: 1024px) 96px, (min-width: 761px) 58px, 44px" width="160" height="160" alt=""></button>`).join('');
      this.selectPhoto(this.set.featuredImage || this.images()[0]);
      this.updateLengths();
      this.updateItemDetails(parts);
      this.showStatus(this.canPurchase() ? '' : this.copy.unavailable);
      this.setBusy(false);
    }

    updateItemDetails(parts) {
      const name = this.find('[data-czb-info-name]');
      if (!name) return;
      name.textContent = this.set.name;
      this.find('[data-czb-info-parts]').innerHTML = parts.map((part) => `<li>${escape(part)}</li>`).join('');
      this.find('[data-czb-info-tone]').textContent = this.set.tone;
      this.find('[data-czb-info-layered]').hidden = this.set.category !== 'layered';
      this.find('[data-czb-info-bracelet]').hidden = this.set.category === 'layered';
    }

    updateInstallments() {
      const placement = this.find('[data-czb-klarna]');
      if (!placement) return;
      const amount = this.set.priceCents;
      const valid = Number.isSafeInteger(amount) && amount > 0;
      this.find('[data-czb-installments]').hidden = !valid;
      if (!valid) return;
      placement.dataset.purchaseAmount = String(amount);

      // Reuse the store's public Klarna merchant identity and official messaging.
      // Provider failures must never interrupt the set selector or checkout.
      try {
        const sdkUrl = 'https://js.klarna.com/web-sdk/v1/klarna.js';
        if (!document.querySelector(`script[src="${sdkUrl}"]`)) {
          const script = document.createElement('script');
          script.src = sdkUrl;
          script.async = true;
          script.dataset.environment = 'production';
          script.dataset.clientId = 'e2a7a758-f0c1-5b7f-b7c5-a28c6228838a';
          document.head.appendChild(script);
        }
        const refresh = window.Klarna?.OnsiteMessaging?.refresh();
        refresh?.catch?.(() => {});
      } catch (_) {
        // Klarna may be unavailable or blocked; purchasing remains available.
      }
    }

    canPurchase() {
      return this.set.available === true && Number.isSafeInteger(this.set.variantId) && this.set.variantId > 0;
    }

    showStatus(message, showCart = false) {
      const status = this.find('[data-czb-purchase-status]');
      status.textContent = message;
      status.hidden = !message;
      this.find('[data-czb-cart-link]').hidden = !showCart;
    }

    setBusy(busy, mode) {
      this.purchasing = busy;
      this.setAttribute('aria-busy', String(busy));
      this.querySelectorAll('[data-czb-set], [data-czb-photo], [data-czb-lengths] select').forEach((element) => { element.disabled = busy; });
      const add = this.find('[data-czb-add-cart]');
      const buy = this.find('[data-czb-buy-now]');
      add.disabled = buy.disabled = busy || !this.canPurchase();
      add.textContent = busy && mode === 'cart' ? this.copy.adding : this.copy.add_cart;
      buy.textContent = busy && mode === 'checkout' ? this.copy.checking_out : this.copy.buy_now;
    }

    toggleFaq(topic) {
      const selected = this.find(`[data-czb-faq-toggle="${topic}"]`);
      if (!selected) return;
      const open = selected.getAttribute('aria-expanded') !== 'true';
      this.querySelectorAll('[data-czb-faq-toggle]').forEach((button) => {
        button.setAttribute('aria-expanded', String(open && button === selected));
      });
      this.querySelectorAll('[data-czb-faq-panel]').forEach((panel) => {
        panel.hidden = !open || panel.dataset.czbFaqPanel !== topic;
      });
    }

    onClick(event) {
      const faqButton = event.target.closest('[data-czb-faq-toggle]');
      if (faqButton) {
        this.toggleFaq(faqButton.dataset.czbFaqToggle);
        return;
      }
      const setLink = event.target.closest('[data-czb-set]');
      if (this.purchasing) {
        if (setLink) event.preventDefault();
        return;
      }
      if (setLink) {
        if (this.dataset.czbNative === 'true' && !window.Shopify?.designMode) return;
        event.preventDefault();
        this.selectSet(setLink.dataset.czbSet);
      }
      const photoButton = event.target.closest('[data-czb-photo]');
      if (photoButton) this.selectPhoto(this.images()[Number(photoButton.dataset.czbPhoto)]);
      if (event.target.closest('[data-czb-add-cart]')) this.purchase('cart');
      if (event.target.closest('[data-czb-buy-now]')) this.purchase('checkout');
    }

    async purchase(mode) {
      if (this.purchasing) return;
      if (!this.canPurchase()) {
        this.showStatus(this.copy.unavailable);
        return;
      }
      const preferences = this.preference();
      const fields = this.lengthFields();
      const missing = fields.filter(({ key, lengths }) => !lengths.some((length) => String(length) === preferences[key]));
      if (missing.length) {
        this.showStatus(this.copy.choose_sizes);
        missing.forEach(({ key }) => this.find(`[data-czb-lengths] select[name="${key}"]`).setAttribute('aria-invalid', 'true'));
        this.find(`[data-czb-lengths] select[name="${missing[0].key}"]`).focus();
        return;
      }
      const properties = Object.fromEntries(fields.map(({ key, label }) => [label, `${preferences[key]} ${this.copy.inches}`]));
      const item = { id: this.set.variantId, quantity: 1, properties };
      this.showStatus('');
      this.setBusy(true, mode);
      let added = false;
      let navigating = false;
      try {
        const response = await fetch(this.dataset.cartAddUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [item] })
        });
        const result = await response.json();
        if (!response.ok) {
          const message = typeof result.description === 'string' ? result.description : this.copy.add_error;
          this.showStatus(message, true);
          return;
        }
        if (!Array.isArray(result.items) || !result.items.some((line) => Number(line.variant_id || line.id) === item.id)) {
          this.showStatus(this.copy.confirm_error, true);
          return;
        }
        added = true;
        if (!this.isConnected) return;
        if (mode === 'checkout') {
          HTMLFormElement.prototype.submit.call(this.find('[data-czb-checkout-form]'));
        } else {
          const cartTrigger = document.querySelector('a.cart-icon.openCartDrawer');
          if (window.matchMedia('(min-width: 768px)').matches && typeof cartDrawerEnable !== 'undefined' && cartDrawerEnable && cartTrigger) {
            cartTrigger.click();
            return;
          }
          window.location.assign(this.dataset.cartUrl);
        }
        navigating = true;
      } catch (error) {
        this.showStatus(added ? this.copy.checkout_error : this.copy.confirm_error, true);
      } finally {
        if (!navigating) this.setBusy(false);
      }
    }

  }

  customElements.define('sn-cz-builder', SnCzBuilder);
})();


